import type { Component } from "solid-js";
import { NumberInput } from "~/solidJs/shared/ui";

interface SeatsNumberInputProps {
	value: string;
	min: number;
	max?: number;
	onChange: (value: string) => void;
	onIncrement?: () => void;
	onDecrement?: () => void;
}

/**
 * Seats number input with min/max value validation
 * - Allows any input while typing (to enable entering "10" by first typing "1")
 * - Only updates form if value >= min and <= max (if max is provided)
 * - Clamps to min/max on blur if value is invalid
 */
export const SeatsNumberInput: Component<SeatsNumberInputProps> = (props) => {
	let containerRef: HTMLDivElement | undefined;

	const handleValueChange = (details: { value: string; valueAsNumber: number }) => {
		const isValid =
			details.valueAsNumber >= props.min &&
			(props.max === undefined || details.valueAsNumber <= props.max);

		if (isValid) {
			props.onChange(details.value);
		}
	};

	const handleFocusOut = (e: FocusEvent) => {
		const relatedTarget = e.relatedTarget as Node | null;
		// Don't validate if focus moved to increment/decrement buttons
		if (containerRef && relatedTarget && containerRef.contains(relatedTarget)) {
			return;
		}

		const input = containerRef?.querySelector("input") as HTMLInputElement | null;
		if (!input) return;

		const inputValue = input.value;
		const numValue = Number.parseInt(inputValue, 10);

		// Only update if value is actually invalid
		if (inputValue === "" || Number.isNaN(numValue)) {
			if (inputValue !== String(props.min)) {
				input.value = String(props.min);
				props.onChange(String(props.min));
			}
		} else if (numValue < props.min) {
			if (inputValue !== String(props.min)) {
				input.value = String(props.min);
				props.onChange(String(props.min));
			}
		} else if (props.max !== undefined && numValue > props.max) {
			if (inputValue !== String(props.max)) {
				input.value = String(props.max);
				props.onChange(String(props.max));
			}
		}
	};

	return (
		<div ref={containerRef} onFocusOut={handleFocusOut}>
			<NumberInput.NumberInputClosed
				rootProps={{
					value: props.value,
					onValueChange: handleValueChange,
					min: props.min,
					max: props.max
				}}
				variant="seats"
				onIncrement={props.onIncrement}
				onDecrement={props.onDecrement}
			/>
		</div>
	);
};
